import React from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';

import {
  Entry,
  EntryPart,
  QueryBehaviorEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {Segmentation} from 'common/types/reports/query';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
// eslint-disable-next-line import/no-cycle
import {FilterParts} from 'common/query-builder/query-entry-section/row-types/filter/parts';
import {BehaviorRowDateRange} from 'common/query-builder/query-entry-section/components/behaviors';
import {EntryRowProps} from 'common/query-builder/query-entry-section/row-types/types';
import {SelectTheme} from 'common/components/select/constants';
import {SelectReact} from 'common/components/select';
import {isTimeSeriesChartType} from 'common/report/insights/visualization/charts/chart-display/utils';

import {BehavioralSubItem, BehavioralTimeReference, PropertySelectorProps} from '../template';
import styles from './index.module.styl';

export function AggregateUserFrequencyByGroupSubItem(
  props: PropertySelectorProps & {
    contextPanelParentElement: HTMLElement | HTMLDivElement;
    isFilter: boolean;
    extraFilters?: React.ReactNode;
  },
) {
  const {t} = useTranslation();
  const selected = props.selected as Segmentation;
  const behavior = selected.behavior!;
  const filter = behavior.filters[0];

  return (
    <div
      className={clsx({
        [styles.containerSidebar]: props.hasNewUI,
        [styles.containerCohorts]: !props.hasNewUI,
      })}
    >
      <BehavioralSubItem
        {...props}
        showBehaviorTimeReference={false}
        selected={filter}
        onSelect={(f) => {
          props.onSelect({
            ...selected,
            behavior: {
              ...behavior,
              filters: [f],
            },
          });
          const nextPart = props.isFilter ? EntryPart.FilterValue : EntryPart.NestedBehaviorFilterValue;
          props.setActivePart(nextPart, `open`);
        }}
      />

      <div
        className={clsx({
          [styles.filtersSidebar]: props.hasNewUI,
          [styles.filtersCohort]: !props.hasNewUI,
        })}
      >
        <div className={styles.filterPart}>
          {!props.hasNewUI && (
            <SelectReact
              elref='eventPlaceHolder'
              disableSearch
              menuWidth={200}
              menuHeight={0}
              readonly={false}
              selectedOption={`Total Events`}
              theme={SelectTheme.Select}
              neverShowCaret
              disableTooltip
              sections={[
                {
                  items: [
                    {
                      label: `Total Events`,
                      isSelected: true,
                    },
                  ],
                },
              ]}
              onChange={() => {
                return;
              }}
            />
          )}
          <FilterParts
            filter={new PropertyFilter(filter)}
            hideListQuantifier
            hasNewUI={props.hasNewUI}
            readonly={false}
            unit={props.hasNewUI ? `Times` : undefined}
            eventNames={[behavior.event?.value]}
            projectId={props.sectionProps.projectId!}
            workspaceId={props.sectionProps.workspaceId!}
            userId={props.sectionProps.userId!}
            dataGroupId={props.sectionProps.dataGroupId!}
            updateEntry={(filterValue) => {
              props.onSelect({
                ...selected,
                behavior: {
                  ...selected.behavior,
                  filters: [filterValue.getAttributes()],
                },
              });
            }}
            setActiveEntry={(state) => props.setActivePart(EntryPart.NestedBehaviorFilterValue, state)}
            isValueActive={props.activePart === EntryPart.NestedBehaviorFilterValue}
            hideEvents
          />
          {!props.hasNewUI && (
            <div className={styles.dateSelector}>
              <BehaviorRowDateRange
                {...(props as EntryRowProps<QueryBehaviorEntry>)}
                contextPanelParentElement={props.contextPanelParentElement}
                entry={{
                  type: SelectorTypeDefinitions.Behavior,
                  event: {
                    value: null,
                    label: ``,
                  },
                  dateRange: behavior.dateRange,
                  aggregationOperator: behavior.aggregationOperator,
                  filtersOperator: behavior.filtersOperator,
                }}
                updateEntry={(action, qbe: Entry | undefined, _options) => {
                  qbe = qbe as QueryBehaviorEntry;
                  behavior.dateRange = qbe.dateRange;
                  behavior.filters[0].behavior!.dateRange = qbe.dateRange;
                  return props.updateEntry(action, props.entry);
                }}
              />
            </div>
          )}
        </div>
        <div className={styles.filterPart}>
          {!props.hasNewUI && (
            <SelectReact
              elref='eventPlaceHolder'
              disableSearch
              menuWidth={200}
              menuHeight={0}
              readonly={false}
              selectedOption={`Total Users`}
              theme={SelectTheme.Select}
              neverShowCaret
              disableTooltip
              sections={[
                {
                  items: [
                    {
                      label: `Total Users`,
                      isSelected: true,
                    },
                  ],
                },
              ]}
              onChange={() => {
                return;
              }}
            />
          )}
          {props.extraFilters}
        </div>
      </div>
      {props.hasNewUI && isTimeSeriesChartType(props.currentChartType) && (
        <BehavioralTimeReference
          {...props}
          selected={props.selected}
          onSelect={props.onSelect}
          activePart={props.activePart}
          setActivePart={props.setActivePart}
          currentProject={props.currentProject}
          tooltip={{text: t(`query-builder.behavior-interval.number-users-who-did-tooltop`)}}
        />
      )}
    </div>
  );
}
